import { isNonEmptyString } from '@nora-monorepo/shared';
import type {
  PositionResponse,
  SubscribeEvent,
  TrackApiWebClient,
} from '@nora-monorepo/track-api-web-client';
import {
  useAuthStore,
  useGenericUserStore,
} from '@nora-monorepo/ui-shared';

const INITIAL_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 10000;
const RANDOM_JITTER_MS = 1000; // Random jitter amount to avoid everyone reconnecting at the exact same time if the server restarts.
const RETRY_BACKOFF_MULTIPLIER = 1.5;

export class TracksManager {
  private authStore = useAuthStore();
  private userStore = useGenericUserStore();

  private abortController = new AbortController();
  private retryAttempt = 0;
  private retryDelay = INITIAL_RETRY_DELAY_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionId = '';
  private pingTimeoutMs: number | undefined;

  constructor(
    private readonly client: TrackApiWebClient,
    private readonly onMessage: (track: PositionResponse) => void,
  ) {
    this.retryDelay = this.getNextRetryDelay();
  }

  start(): void {
    this.subscribe();
  }

  stop(): void {
    this.abortController.abort('[TracksManager] Stopped.');
    this.clearPingTimeout();
    this.resetRetryState();
  }

  restart(): void {
    this.stop();
    this.abortController = new AbortController();
    this.subscribe();
  }

  private subscribe(): void {
    try {
      const userId = this.userStore.user?.id;

      if (!isNonEmptyString(userId)) {
        throw new Error('No user ID available for track subscription.');
      }

      const subscription = this.client.subscribeV2(
        {
          subscriberId: userId,
          deviceId: localStorage.getItem('deviceId') ?? '',
        },
        {
          meta: { authorization: `Bearer ${this.authStore.token}` },
          abort: this.abortController.signal,
        },
      );

      subscription.responses.onMessage((event) => {
        this.resetRetryState();
        this.handleSubscriptionEvent(event);
      });
      subscription.responses.onComplete(() => this.handleDisconnection('Completed'));
      subscription.responses.onError(error => this.handleDisconnection('Error', error));
    }
    catch (error) {
      this.handleDisconnection('Failed to subscribe', error);
    }
  }

  private handleDisconnection(reason: string, error?: unknown): void {
    if (this.abortController.signal.aborted) {
      return;
    }

    if (error !== undefined) {
      console.error(`[track-api client] ${reason}:`, error);
    }
    else {
      console.warn(`[track-api client] ${reason}, reconnecting...`);
    }

    this.scheduleRetry();
  }

  private getNextRetryDelay(delay: number = this.retryDelay): number {
    return Math.min(delay * RETRY_BACKOFF_MULTIPLIER + (Math.random() * RANDOM_JITTER_MS), MAX_RETRY_DELAY_MS);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
    }
  }

  private resetPingTimeout(): void {
    this.clearPingTimeout();

    if (this.pingTimeoutMs === undefined || this.pingTimeoutMs <= 0) {
      console.warn('[track-api client] Invalid ping timeout value, skipping ping timeout setup.');

      return;
    }

    this.pingTimeoutTimer = setTimeout(() => {
      console.warn('[track-api client] No ping received within timeout, reconnecting...');

      this.restart();
    }, this.pingTimeoutMs);
  }

  private clearPingTimeout(): void {
    if (this.pingTimeoutTimer === null) {
      return;
    }

    clearTimeout(this.pingTimeoutTimer);

    this.pingTimeoutTimer = null;
  }

  private scheduleRetry(): void {
    if (this.abortController.signal.aborted) {
      return;
    }

    console.warn(`[track-api client] No connection to track-api, attempting to reconnect in ${this.retryDelay.toFixed(0)}ms.`);

    this.retryAttempt++;
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      if (!this.abortController.signal.aborted) {
        this.subscribe();
      }
    }, this.retryDelay);

    this.retryDelay = this.getNextRetryDelay();
  }

  private resetRetryState(): void {
    this.retryAttempt = 0;
    this.retryDelay = this.getNextRetryDelay(INITIAL_RETRY_DELAY_MS);
    this.clearRetryTimer();
  }

  private handleSubscriptionEvent(event: SubscribeEvent): void {
    switch (event.payload.oneofKind) {
      case 'position': {
        this.onMessage(event.payload.position);

        return;
      }

      case 'connected': {
        this.connectionId = event.payload.connected.connectionId;
        this.pingTimeoutMs = event.payload.connected.pingTimeoutMs;
        this.resetPingTimeout();

        console.warn(`[track-api client] Connected successfully. Connection ID: ${this.connectionId}`);

        return;
      }

      case 'ping': {
        this.resetPingTimeout();
        void this.respondWithPong();

        break;
      }

      case 'pong': {
        break;
      }

      case undefined:
      default:
    }
  }

  private async respondWithPong(): Promise<void> {
    try {
      await this.client.pong(
        {
          connectionId: this.connectionId,
          timestamp: new Date().toISOString(),
        },
        {
          meta: { authorization: `Bearer ${this.authStore.token}` },
        },
      );
    }
    catch (error) {
      console.error(`[track-api client] Failed to send pong for connection ${this.connectionId}:`, error);
    }
  }
}
